// src/pages/VibeResourcesPage.jsx - ORIGINAL CONTENT with darker background for readability
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone,
  MessageCircle,
  BookOpen,
  Users,
  AlertTriangle,
  Heart,
  Brain,
  ExternalLink,
  Clock,
  MapPin,
  Stethoscope,
  UserCheck,
  Shield,
  Star,
  Globe,
  Mail,
  Headphones,
  Video,
  Smartphone,
  Instagram,
  Badge,
  Youtube,
  Sparkles,
  Flame,
  Zap,
  Coffee,
  GamepadIcon,
} from 'lucide-react';
import { RESOURCES_BY_COUNTRY, DEFAULT_COUNTRY } from '../data/crisisSupport';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';

const VibeResourcesPage = () => {
  const [activeCountry, setActiveCountry] = useState(DEFAULT_COUNTRY);
  const [activeSection, setActiveSection] = useState('crisis');

  // Crisis support by country: the fleet's one table (src/data/crisisSupport.js),
  // with this page's Teen Specialist badge. Nothing is typed here.
  const crisisSupport = RESOURCES_BY_COUNTRY;

  // Teen-focused mental health guidance
  const teenMentalHealth = {
    redFlags: [
      {
        title: 'Your Inner Voice is Constantly Mean',
        description: 'When that voice in your head is non-stop negative and it\'s affecting your daily life for weeks.',
        severity: 'moderate',
        emoji: '💭',
      },
      {
        title: 'Can\'t Do Normal Stuff',
        description: 'When negative self-talk makes it hard to go to school, hang with friends, or do things you used to enjoy.',
        severity: 'moderate',
        emoji: '😔',
      },
      {
        title: 'Thoughts About Hurting Yourself',
        description: 'ANY thoughts about self-harm or not wanting to be here need immediate help.',
        severity: 'urgent',
        emoji: '🚨',
      },
      {
        title: 'Using Substances to Feel Better',
        description: 'Drinking, drugs, or other substances to quiet negative thoughts or feel better.',
        severity: 'urgent',
        emoji: '⚠️',
      },
      {
        title: 'Avoiding Everyone',
        description: 'Isolating from friends and family because you feel worthless or different.',
        severity: 'moderate',
        emoji: '🏠',
      },
      {
        title: 'Physical Symptoms from Stress',
        description: 'Racing heart, panic attacks, or physical symptoms when your thoughts spiral.',
        severity: 'moderate',
        emoji: '💓',
      },
    ],
    gettingHelp: [
      {
        title: 'Talk to Someone You Trust First',
        description: 'A parent, teacher, school counsellor, or close friend can help you figure out next steps.',
        steps: [
          'Choose someone who\'s been supportive before',
          'Pick a time when you won\'t be interrupted',
          'Be honest about what you\'re experiencing',
          'Ask them to help you find professional support',
        ],
        icon: Heart,
      },
      {
        title: 'School Counsellor or Psychologist',
        description: 'Many schools have mental health professionals who understand teen issues.',
        steps: [
          'Ask how to make an appointment',
          'Know that they\'re trained to help with exactly these issues',
          'They can connect you with outside help if needed',
          'Everything you share is confidential (with rare safety exceptions)',
        ],
        icon: UserCheck,
      },
      {
        title: 'Find a Teen-Friendly Therapist',
        description: 'Therapists who specialise in teens understand your world better.',
        steps: [
          'Look for "adolescent" or "teen" specialists',
          'Many offer online sessions if that feels more comfortable',
          'Ask about their approach - CBT is great for negative self-talk',
          'It\'s okay to "shop around" until you find the right fit',
        ],
        icon: Brain,
      },
    ],
  };

  // Teen-relevant books and resources
  const teenResources = {
    books: [
      {
        title: 'Mind Over Mood for Teens',
        author: 'Dennis Greenberger & Christine Padesky',
        description: 'CBT workbook designed specifically for teenagers dealing with anxiety and depression.',
        category: 'Workbooks',
        rating: 4.8,
        reason: 'Written for teens, with relatable examples and practical exercises',
        vibeLevel: '🔥',
      },
      {
        title: 'Self-Compassion for Teens',
        author: 'Karen Bluth',
        description: 'Learn to be kind to yourself instead of your own worst critic.',
        category: 'Self-Compassion',
        rating: 4.7,
        reason: 'Perfect for teens who are super hard on themselves',
        vibeLevel: '💖',
      },
      {
        title: 'The Mindful Teen',
        author: 'Dzung Vo',
        description: 'Practical mindfulness techniques that actually work for busy teen lives.',
        category: 'Mindfulness',
        rating: 4.6,
        reason: 'No boring meditation - just practical stuff that fits your actual life',
        vibeLevel: '🧘‍♀️',
      },
      {
        title: 'Feeling Better: CBT Workbook for Teens',
        author: 'John Sharry',
        description: 'Step-by-step guide to understanding and changing negative thought patterns.',
        category: 'CBT',
        rating: 4.7,
        reason: 'Super practical with real teen scenarios and examples',
        vibeLevel: '⚡',
      },
      {
        title: 'The Anxiety and Worry Workbook for Teens',
        author: 'Lisa Schab',
        description: 'CBT activities to help you gain confidence and conquer fear.',
        category: 'Anxiety',
        rating: 4.5,
        reason: 'Great for teens whose inner voice is anxious and worried',
        vibeLevel: '🛡️',
      },
      {
        title: 'Why Has Nobody Told Me This Before?',
        author: 'Dr. Julia Smith',
        description: 'Practical psychology tools for everyday life - written by a psychologist who really gets it.',
        category: 'Practical Psychology',
        rating: 4.9,
        reason: 'Amazing practical advice in super accessible language - feels like getting advice from your coolest therapist friend',
        vibeLevel: '🌟',
      },
      {
        title: 'It\'s Not That Deep',
        author: 'Dr. Julia Smith',
        description: 'How to stop overthinking and start living - perfect for when your brain won\'t stop.',
        category: 'Overthinking & Anxiety',
        rating: 4.8,
        reason: 'Tackles overthinking with humor and real solutions - written specifically for people who think too much',
        vibeLevel: '🧠',
      },
    ],
    apps: [
      {
        name: 'Headspace for Teens',
        type: 'Meditation & Mindfulness',
        description: 'Age-appropriate meditations for stress, focus, and self-compassion.',
        platforms: ['iOS', 'Android'],
        cost: 'Free with premium options',
        vibeRating: '🌟🌟🌟🌟🌟',
      },
      {
        name: 'Calm',
        type: 'Sleep & Relaxation',
        description: 'Sleep stories, meditation, and anxiety management tools.',
        platforms: ['iOS', 'Android', 'Web'],
        cost: 'Free with premium',
        vibeRating: '🌟🌟🌟🌟',
      },
      {
        name: 'Sanvello',
        type: 'Mood Tracking',
        description: 'Track your mood, practice CBT techniques, and get peer support.',
        platforms: ['iOS', 'Android'],
        cost: 'Free with premium',
        vibeRating: '🌟🌟🌟🌟',
      },
      {
        name: 'MindShift',
        type: 'Anxiety Support',
        description: 'CBT-based strategies for managing anxiety and worry.',
        platforms: ['iOS', 'Android'],
        cost: 'Free',
        vibeRating: '🌟🌟🌟🌟🌟',
      },
    ],
    socialMedia: [
      {
        platform: 'TikTok',
        account: '@therapy.with.katie',
        description: 'Licensed therapist sharing bite-sized mental health tips',
        followers: '2M+',
        vibes: '✨ Relatable ✨',
      },
      {
        platform: 'Instagram',
        account: '@the.anxiety.healer',
        description: 'Anxiety management techniques and self-compassion content',
        followers: '500K+',
        vibes: '🌈 Positive 🌈',
      },
      {
        platform: 'YouTube',
        account: 'Kati Morton',
        description: 'Licensed therapist explaining mental health topics clearly',
        followers: '1M+',
        vibes: '🧠 Educational 🧠',
      },
      {
        platform: 'TikTok',
        account: '@psychedinsf',
        description: 'Psychologist sharing evidence-based mental health content',
        followers: '800K+',
        vibes: '🔬 Science-based 🔬',
      },
      {
        platform: 'YouTube',
        account: 'Dr. Julia Smith',
        description: 'Psychologist with amazing practical advice - explains complex psychology in simple, relatable ways',
        followers: '500K+',
        vibes: '💡 Practical Wisdom 💡',
      },
      {
        platform: 'Instagram',
        account: '@drjuliasmith',
        description: 'Daily psychology tips and myth-busting from a qualified psychologist',
        followers: '300K+',
        vibes: '✨ Real Psychology ✨',
      },
    ],
    podcasts: [
      {
        name: 'Terrible, Thanks for Asking',
        host: 'Nora McInerny',
        description: 'Real talk about life\'s difficult moments - honest and relatable',
        vibes: '💯 Real Talk',
      },
      {
        name: 'On Purpose with Jay Shetty',
        host: 'Jay Shetty',
        description: 'Mindfulness and purpose-driven living for young adults',
        vibes: '🚀 Inspiring',
      },
      {
        name: 'The Hilarious World of Depression',
        host: 'John Moe',
        description: 'Comedians and celebrities talk honestly about mental health',
        vibes: '😂 Funny but Deep',
      },
    ],
  };

  const CrisisSection = () => (
    <div className="space-y-6">
      {/* Country Selector with enhanced design */}
      <Card className="p-6 bg-gradient-to-r from-vibe-coral/10 to-vibe-sunset/10 border-vibe-coral/30">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-vibe-coral" />
          Where are you? Let's find local support 🌍
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(crisisSupport).map(([key, country]) => (
            <button
              key={key}
              onClick={() => setActiveCountry(key)}
              className={`p-3 rounded-xl border transition-all text-left hover-lift ${
                activeCountry === key
                  ? 'border-vibe-coral bg-gradient-to-br from-vibe-coral/20 to-vibe-sunset/20 shadow-glow-coral'
                  : 'border-white/20 hover:border-vibe-coral/50 bg-white/5'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl">{country.flag}</span>
                <span className="text-white font-medium">{country.name}</span>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* Crisis Services with teen-friendly design */}
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Phone className="w-6 h-6 text-vibe-coral" />
          Crisis Support - {crisisSupport[activeCountry].name}
          <Sparkles className="w-5 h-5 text-vibe-gold animate-sparkle" />
        </h3>
        <div className="grid gap-4">
          {crisisSupport[activeCountry].services.map((service, index) => (
            <motion.div 
              key={index} 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-xl border transition-all hover-lift ${
                service.teenSpecific 
                  ? 'bg-gradient-to-r from-vibe-mint/10 to-vibe-sky/10 border-vibe-mint/30' 
                  : 'bg-white/5 border-white/10'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-white font-semibold text-lg">{service.name}</h4>
                    {service.teenSpecific && (
                      <span className="px-2 py-1 bg-vibe-mint/20 text-vibe-mint text-xs rounded-full font-medium">
                        Teen Focused
                      </span>
                    )}
                    {service.popular && (
                      <span className="px-2 py-1 bg-vibe-gold/20 text-vibe-gold text-xs rounded-full font-medium flex items-center gap-1">
                        <Flame className="w-3 h-3" />
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      {service.type === 'call' && <Phone className="w-4 h-4 text-vibe-mint" />}
                      {service.type === 'text' && <MessageCircle className="w-4 h-4 text-vibe-sky" />}
                      {service.type === 'chat' && <MessageCircle className="w-4 h-4 text-vibe-lavender" />}
                      <span className="text-xl font-bold text-vibe-coral">{service.number}</span>
                    </div>
                    {service.hasChat && (
                      <span className="px-2 py-1 bg-vibe-sky/20 text-vibe-sky text-xs rounded">
                        + Chat Available
                      </span>
                    )}
                    {service.hasText && (
                      <span className="px-2 py-1 bg-vibe-lavender/20 text-vibe-lavender text-xs rounded">
                        + Text Available
                      </span>
                    )}
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">{service.description}</p>
                </div>
                <a
                  href={`https://${service.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-vibe-coral hover:text-vibe-sunset transition-colors p-2 rounded-lg hover:bg-white/10"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-2">
                <Globe className="w-3 h-3" />
                {service.website}
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-8 p-4 bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/30 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <span className="text-red-300 font-bold text-lg">In an Emergency 🚨</span>
          </div>
          <p className="text-slate-200 mb-3">
            If you or someone else is in immediate danger, call emergency services right away:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {Object.entries(crisisSupport).map(([key, country]) => (
              <div key={key} className="text-center p-2 bg-red-500/20 rounded-lg">
                <div className="font-bold text-white">{country.flag} {country.name}</div>
                <div className="text-red-300 text-lg font-bold">{country.emergency.number}</div>
              </div>
            ))}
            <div className="text-center p-2 bg-red-500/20 rounded-lg">
              <div className="font-bold text-white">Anywhere else</div>
              <div className="text-red-300 text-sm font-bold">Your local emergency number</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const ProfessionalHelpSection = () => (
    <div className="space-y-6">
      {/* When to Get Help */}
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-vibe-gold" />
          When to Get Professional Help 🤔
        </h3>
        <div className="space-y-4">
          {teenMentalHealth.redFlags.map((flag, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-xl border hover-lift transition-all ${
                flag.severity === 'urgent' 
                  ? 'bg-gradient-to-r from-red-900/30 to-orange-900/30 border-red-500/40'
                  : 'bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-500/40'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="text-3xl">{flag.emoji}</div>
                <div className="flex-1">
                  <h4 className={`font-bold text-lg mb-2 ${
                    flag.severity === 'urgent' ? 'text-red-300' : 'text-yellow-300'
                  }`}>
                    {flag.title}
                  </h4>
                  <p className="text-slate-200 leading-relaxed">{flag.description}</p>
                  {flag.severity === 'urgent' && (
                    <div className="mt-3 flex items-center gap-2 text-red-200 font-medium">
                      <Zap className="w-4 h-4" />
                      <span>This needs immediate attention</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Card>

      {/* How to Get Help */}
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Heart className="w-6 h-6 text-vibe-mint" />
          How to Actually Get Help 💚
        </h3>
        <div className="space-y-6">
          {teenMentalHealth.gettingHelp.map((guide, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15 }}
              className="p-5 bg-gradient-to-r from-white/5 to-white/10 rounded-xl border border-white/10"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-vibe-mint to-vibe-sky rounded-xl flex items-center justify-center flex-shrink-0">
                  <guide.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-bold text-lg mb-2">{guide.title}</h4>
                  <p className="text-slate-300 mb-4 leading-relaxed">{guide.description}</p>
                  <div className="space-y-2">
                    {guide.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-vibe-coral/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-vibe-coral text-sm font-bold">{stepIndex + 1}</span>
                        </div>
                        <span className="text-slate-300 text-sm leading-relaxed">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Card>
    </div>
  );

  const ResourcesSection = () => (
    <div className="space-y-6">
      {/* Books */}
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-vibe-lavender" />
          Books That Actually Get It 📚
        </h3>
        <div className="grid gap-4">
          {teenResources.books.map((book, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 bg-white/5 rounded-xl border border-white/10 hover-lift transition-all"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-white font-bold">{book.title}</h4>
                    <span className="text-lg">{book.vibeLevel}</span>
                  </div>
                  <p className="text-slate-400 text-sm">by {book.author}</p>
                  <span className="inline-block px-3 py-1 bg-vibe-lavender/20 text-vibe-lavender text-xs rounded-full mt-2 font-medium">
                    {book.category}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-vibe-gold fill-current" />
                  <span className="text-vibe-gold font-medium">{book.rating}</span>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-3 leading-relaxed">{book.description}</p>
              <div className="text-sm">
                <span className="text-vibe-mint font-medium">Why it's great:</span>
                <span className="text-slate-300 ml-2">{book.reason}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </Card>

      {/* Apps */}
      <Card className="p-6 glassmorphism">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <Smartphone className="w-6 h-6 text-vibe-sky" />
          Apps That Don't Suck 📱
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {teenResources.apps.map((app, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 bg-white/5 rounded-xl border border-white/10 hover-lift transition-all"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="text-white font-bold text-lg">{app.name}</h4>
                  <span className="text-vibe-sky text-sm font-medium">{app.type}</span>
                </div>
                <div className="text-right">
                  <div className="text-lg">{app.vibeRating}</div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    app.cost.includes('Free') 
                      ? 'bg-vibe-mint/20 text-vibe-mint'
                      : 'bg-vibe-gold/20 text-vibe-gold'
                  }`}>
                    {app.cost}
                  </span>
                </div>
              </div>
              <p className="text-slate-300 text-sm mb-3 leading-relaxed">{app.description}</p>
              <div className="flex flex-wrap gap-1">
                {app.platforms.map((platform, idx) => (
                  <span key={idx} className="px-2 py-1 bg-vibe-coral/20 text-vibe-coral text-xs rounded font-medium">
                    {platform}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </Card>

      {/* Social Media & Podcasts */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6 glassmorphism">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-vibe-sunset" />
            Actually Good Social Media 📲
          </h3>
          <div className="space-y-4">
            {teenResources.socialMedia.map((account, index) => (
              <div key={index} className="p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  {account.platform === 'TikTok' && <div className="w-4 h-4 bg-black rounded text-white text-xs flex items-center justify-center">T</div>}
                  {account.platform === 'Instagram' && <Instagram className="w-4 h-4 text-pink-400" />}
                  {account.platform === 'YouTube' && <Youtube className="w-4 h-4 text-red-400" />}
                  <span className="text-white font-medium">{account.account}</span>
                  <span className="text-xs text-slate-400">({account.followers})</span>
                </div>
                <p className="text-slate-300 text-sm mb-2">{account.description}</p>
                <span className="text-xs px-2 py-1 bg-vibe-sunset/20 text-vibe-sunset rounded-full">
                  {account.vibes}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 glassmorphism">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Headphones className="w-5 h-5 text-vibe-mint" />
            Podcasts Worth Your Time 🎧
          </h3>
          <div className="space-y-4">
            {teenResources.podcasts.map((podcast, index) => (
              <div key={index} className="p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-vibe-mint rounded-full"></div>
                  <span className="text-white font-medium">{podcast.name}</span>
                </div>
                <p className="text-slate-400 text-xs mb-2">with {podcast.host}</p>
                <p className="text-slate-300 text-sm mb-2">{podcast.description}</p>
                <span className="text-xs px-2 py-1 bg-vibe-mint/20 text-vibe-mint rounded-full">
                  {podcast.vibes}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-32 vibe-scrollbar">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header with more personality */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="relative">
              <h1 className="text-4xl md:text-5xl font-bold text-gradient-energy mb-4">
                Support & Resources
              </h1>
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                className="absolute -top-2 -right-4 text-3xl"
              >
                💫
              </motion.div>
            </div>
            <p className="text-lg text-slate-200 max-w-3xl mx-auto leading-relaxed">
              Real support when you need it most. No judgment, just help. 
              Plus resources that actually understand what it's like to be your age. 🤝
            </p>
          </motion.div>

          {/* Enhanced Navigation */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-8"
          >
            <div className="glassmorphism rounded-2xl p-2 flex gap-1 flex-wrap">
              {[
                { id: 'crisis', label: 'Need Help Now', icon: Phone, color: 'text-red-300' },
                { id: 'professional', label: 'Getting Support', icon: Heart, color: 'text-green-300' },
                { id: 'resources', label: 'Cool Resources', icon: Sparkles, color: 'text-purple-300' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 ${
                    activeSection === tab.id
                      ? 'bg-gradient-to-r from-vibe-coral to-vibe-sunset text-white shadow-vibe-md'
                      : 'text-slate-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <tab.icon className={`w-5 h-5 ${activeSection === tab.id ? 'text-white' : tab.color}`} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Content with enhanced animations */}
          <AnimatePresence mode="wait">
            {activeSection === 'crisis' && (
              <motion.div
                key="crisis"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <CrisisSection />
              </motion.div>
            )}

            {activeSection === 'professional' && (
              <motion.div
                key="professional"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ProfessionalHelpSection />
              </motion.div>
            )}

            {activeSection === 'resources' && (
              <motion.div
                key="resources"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ResourcesSection />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Enhanced Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-12"
          >
            <Card className="p-6 glassmorphism border-vibe-sky/30">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-vibe-sky to-vibe-lavender rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
                    Important Stuff 📝
                    <Coffee className="w-5 h-5 text-vibe-gold" />
                  </h3>
                  <p className="text-slate-200 leading-relaxed">
                    This app and these resources are here to support you, but they're not a replacement for professional help when you really need it. 
                    If you're having thoughts of self-harm, feel like you're in crisis, or your mental health is seriously impacting your life, 
                    please reach out to a professional or emergency services. You deserve support, and there are people trained to help. 💙
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-sm text-vibe-mint">
                    <Zap className="w-4 h-4" />
                    <span className="font-medium">Remember: Asking for help is actually really brave. 🦸‍♀️</span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VibeResourcesPage;